import { ChangeDetectionStrategy, ChangeDetectorRef, Component, CUSTOM_ELEMENTS_SCHEMA, DestroyRef, inject, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IonicModule } from '@ionic/angular';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription, take } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { WindowService } from 'src/shared/providers/window.service';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { Util } from 'src/shared/util/util';
import { Client } from 'src/providers/client/client';

import { TidyUiComponentsModule } from 'src/tidy-ui-components/tidy-ui-components.module';
import { SharedModule } from 'src/shared/shared.module';

import { AddEditHappinessItemPage } from 'src/pages/marketing/add-edit-happiness-item/add-edit-happiness-item';
import { HappinessItemsService } from 'src/shared/services/happiness-items.service';
import { DemoBlockService } from 'src/shared/services/demo-block.service';
import { getHappinessIconSrc } from 'src/pages/marketing/components/shared/happiness-visuals.util';
import { HappinessItem } from 'src/models/happiness-item.model';
import { HappinessCardComponent, HappinessFlag } from 'src/pages/marketing/components/happiness-card/happiness-card.component';

@Component({
  selector: 'happiness-tab',
  templateUrl: './happiness-tab.component.html',
  styleUrls: ['./happiness-tab.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, IonicModule, TidyUiComponentsModule, SharedModule, HappinessCardComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class HappinessTabComponent implements OnInit, OnDestroy {
  readonly windowService = inject(WindowService);
  private readonly fb = inject(FormBuilder);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly client = inject(Client);
  private readonly navCtrl = inject(CustomNavController);
  private readonly rightSidePanelService = inject(RightSidePanelService);
  private readonly storage = inject(TidyStorage);
  private readonly util = inject(Util);
  private readonly happinessItemsService = inject(HappinessItemsService);
  private readonly demoBlock = inject(DemoBlockService);

  form = this.fb.group({
    selectedAddress: [0],
    search: [''],
  });

  isDataLoaded = false;
  errorMessage = '';
  addressFilter: { value: number; viewValue: string }[] = [];
  items: HappinessItem[] = [];
  rawItems: HappinessItem[] = [];
  /** Desktop renders through the shared tidy-table; mobile keeps the card grid. */
  readonly headers = ['Name', 'Allow Upsell', 'Allow Comp', 'last-column'];
  readonly toggleColumns: Record<string, HappinessFlag> = {
    'Allow Upsell': 'can_upsell',
    'Allow Comp': 'can_comp',
  };
  rows: any[] = [];
  private searchTerm = '';
  private panelClosedSubscription$?: Subscription;

  async ngOnInit() {
    this.form.get('search')?.valueChanges
      .pipe(debounceTime(150), takeUntilDestroyed(this.destroyRef))
      .subscribe(value => {
        this.searchTerm = (value ?? '').trim().toLowerCase();
        this.setItems(this.rawItems);
        this.cdr.markForCheck();
      });
    try {
      await this.buildAddressFilter();
      await this.loadPage();
    } catch (err) {
      this.errorMessage = err?.error?.message || err?.message || 'Failed to load offerings';
      this.util.showError(this.errorMessage, 8000);
      // Settle the skeleton into the empty state instead of looping forever.
      this.isDataLoaded = true;
      this.cdr.markForCheck();
    }
  }

  ngOnDestroy() {
    this.panelClosedSubscription$?.unsubscribe();
  }

  async loadPage() {
    try {
      const addressId = this.form.value.selectedAddress || undefined;
      const fetched = await this.happinessItemsService.getHappinessItems(addressId);
      this.rawItems = [...fetched].sort((a, b) => a.name.localeCompare(b.name));
    } catch {
      this.rawItems = [];
    }
    this.setItems(this.rawItems);
    this.isDataLoaded = true;
    this.cdr.markForCheck();
  }

  private setItems(list: HappinessItem[]) {
    this.items = this.applyFilter(list);
    this.rows = this.buildRows(this.items);
  }

  private applyFilter(list: HappinessItem[]): HappinessItem[] {
    if (!this.searchTerm) {
      return list;
    }
    return list.filter(i => i.name.toLowerCase().includes(this.searchTerm));
  }

  buildRows(items: HappinessItem[]): any[] {
    return items.map(item => ({
      id: item.id,
      item,
      'Name': { value: this.nameCellHtml(item), sortValue: item.name.toLowerCase() },
      'Allow Upsell': { value: '', toggledOn: item.can_upsell },
      'Allow Comp': { value: '', toggledOn: item.can_comp },
      'last-column': { value: '', hasChevron: true },
    }));
  }

  // The same tinted icon tile the mobile cards use, inline before the name (the
  // table renders cell values via innerHTML, so this survives sanitization). A
  // cell `icon` would instead render a bare 18px image with no tile behind it.
  private nameCellHtml(item: HappinessItem): string {
    const icon = getHappinessIconSrc(item);
    const tile = 'display:inline-flex;align-items:center;justify-content:center;flex-shrink:0;width:32px;height:32px;margin-right:8px;border-radius:6px;background:rgba(65,202,183,0.18);vertical-align:middle;';
    const glyph = 'width:18px;height:18px;object-fit:contain;filter:brightness(0) saturate(100%) invert(70%) sepia(45%) saturate(450%) hue-rotate(125deg);';
    return `<span style="${tile}"><img src="${icon}" alt="" style="${glyph}"></span>${this.escapeHtml(item.name)}`;
  }

  // Offering names are customer-entered, and cell values bypass Angular's
  // sanitizer, so escape before interpolating into the cell markup.
  private escapeHtml(value: string): string {
    const map: Record<string, string> = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
    return (value ?? '').replace(/[&<>"']/g, char => map[char]);
  }

  onFilterValueChange() {
    this.loadPage();
  }

  onFlagChange(event: { item: HappinessItem; flag: HappinessFlag; value: boolean }) {
    const { item, flag, value } = event;
    const previousValue = item[flag];
    item[flag] = value;
    this.cdr.markForCheck();
    this.happinessItemsService.updateHappinessItem(item.id, { [flag]: value })
      .catch(err => {
        item[flag] = previousValue;
        // The table already flipped its own cell, so rebuild the rows to put it back.
        this.rows = this.buildRows(this.items);
        this.errorMessage = err?.error?.message || err?.message || 'Failed to update offering';
        this.util.showError(this.errorMessage, 5000);
        this.cdr.markForCheck();
      });
  }

  onTableToggleChange(event: { checked: boolean; column: string; element: any }) {
    const item = event.element?.item as HappinessItem;
    const flag = this.toggleColumns[event.column];
    if (!item || !flag) { return; }
    if (this.demoBlock.block()) {
      // Rebuild from the untouched item so the blocked toggle snaps back.
      this.rows = this.buildRows(this.items);
      this.cdr.markForCheck();
      return;
    }
    this.onFlagChange({ item, flag, value: event.checked });
  }

  onTableColumnClick(event: { column: string; element: any }) {
    // Toggle cells own their own click; only the rest of the row opens the offering.
    if (this.toggleColumns[event.column]) { return; }
    const item = event.element?.item as HappinessItem;
    if (!item) { return; }
    if (this.demoBlock.block()) { return; }
    this.onEditItem(item);
  }

  sortChanged(sort: any) {
    if (!sort?.active || !sort?.direction) { return; }
    const multiplier = sort.direction === 'asc' ? 1 : -1;
    this.rows = [...this.rows].sort((a, b) => {
      const valueA = this.cellSortValue(a[sort.active]);
      const valueB = this.cellSortValue(b[sort.active]);
      if (valueA < valueB) { return -1 * multiplier; }
      if (valueA > valueB) { return multiplier; }
      return 0;
    });
    this.cdr.markForCheck();
  }

  // Name sorts on the plain text (its value is icon-tile markup) and the flag
  // columns sort on the toggle state, which has no `value` at all.
  private cellSortValue(cell: any): string {
    if (!cell) { return ''; }
    if (cell.sortValue !== undefined) { return String(cell.sortValue); }
    if (cell.toggledOn !== undefined) { return cell.toggledOn ? '1' : '0'; }
    return String(cell.value ?? '').toLowerCase();
  }

  onEditItem(item: HappinessItem) {
    this.goToPage('add-edit-happiness-item', { item }, AddEditHappinessItemPage);
  }

  goToAddItemPage() {
    if (this.demoBlock.block()) { return; }
    const params: Record<string, any> = {};
    const addressId = this.form.value.selectedAddress;
    if (addressId) {
      params.addressId = addressId;
    }
    this.goToPage('add-edit-happiness-item', params, AddEditHappinessItemPage);
  }

  private async buildAddressFilter() {
    const addresses = await this.client.getAddresses();
    this.addressFilter = this.client.parseAddressList(addresses, false);
    const firstAddressId = this.addressFilter[0]?.value ?? 0;
    this.form.patchValue({ selectedAddress: firstAddressId }, { emitEvent: false });
    this.cdr.markForCheck();
  }

  private async goToPage(url: string, params: any, component: any) {
    if (this.windowService.isDesktopRes) {
      const isPanelOpen = await this.storage.retrieve('dialog-right-side-open');
      if (isPanelOpen) {
        this.rightSidePanelService.navigateTo(url, params, component);
      } else {
        this.storage.save('dialog-right-side-open', true);
        this.storage.save('dialog-params', params);
        this.rightSidePanelService.openRightSidePanel(component);
      }
      this.panelClosedSubscription$?.unsubscribe();
      this.panelClosedSubscription$ = this.rightSidePanelService.panelClosed().pipe(take(1)).subscribe(async () => {
        const didMakeChanges = await this.storage.retrieve('didMakeHappinessItemChanges');
        if (didMakeChanges) {
          await this.storage.save('didMakeHappinessItemChanges', false);
          await this.loadPage();
        }
      });
    } else {
      // Mobile: AddEdit page reads dialog-params from TidyStorage, so seed
      // it here. Mark dialog-right-side-open false so the page renders the
      // mobile top-nav layout instead of the right-side ion-content.
      await this.storage.save('dialog-right-side-open', false);
      await this.storage.save('dialog-params', params);
      this.navCtrl.navigateForward(url, params);
    }
  }
}
